import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'Components/Atoms/Box';
import Button from 'Components/Atoms/Button';
import Checkbox from 'Components/Atoms/Checkbox';
import Typography from 'Components/Atoms/Typography';
import useRestaurant from 'CustomHooks/useRestaurant';
import { EditReservation } from 'types/reservations';
import { fetchReservationProtection } from 'App/NoShowProtection/api';
import { staffProtectionError } from 'App/NoShowProtection/domain';

const NS = 'noShowProtection';

type StaffProtectionOptionProps = {
  reservation: EditReservation;
  onReservationChange: React.Dispatch<
    React.SetStateAction<EditReservation | null>
  >;
};

const StaffProtectionOption: React.FC<StaffProtectionOptionProps> = ({
  reservation,
  onReservationChange,
}) => {
  const { t } = useTranslation(NS);
  const { restaurantId, apps } = useRestaurant();
  const appActive = apps.includes('noShowMollie');
  const { reservationId } = reservation;
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!appActive || !restaurantId || !reservationId) return;
    let cancelled = false;
    setLoadFailed(false);
    fetchReservationProtection(restaurantId, reservationId)
      .then(({ matchingTemplate, canCharge }) => {
        if (cancelled) return;
        onReservationChange((old) =>
          !old || old.reservationId !== reservationId
            ? old
            : {
                ...old,
                noShowProtectionTemplate: matchingTemplate,
                noShowProtectionCanCharge: canCharge,
              }
        );
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [appActive, restaurantId, reservationId, onReservationChange, attempt]);

  useEffect(() => {
    onReservationChange((old) =>
      !old || !old.noShowProtectionValidationError
        ? old
        : { ...old, noShowProtectionValidationError: undefined }
    );
  }, [
    reservation.guest?.phone,
    reservation.guest?.sendSms,
    reservation.noShowProtectionWaive,
    onReservationChange,
  ]);

  const template = reservation.noShowProtectionTemplate;
  if (!appActive || template === null || reservation.addToWaitinglist)
    return null;
  const canCharge = !!reservation.noShowProtectionCanCharge;
  const waived = canCharge && !!reservation.noShowProtectionWaive;
  const error =
    reservation.noShowProtectionValidationError ||
    staffProtectionError({
      waive: waived,
      canCharge,
      phone: reservation.guest?.phone,
      sendSms: reservation.guest?.sendSms,
    });

  return (
    <Box id="staff-no-show-protection" className="staff-no-show-protection">
      <Typography variant="text-2" translation={null} block>
        {t('No-show protection')}
      </Typography>
      {!template ? (
        <>
          <div role={loadFailed ? 'alert' : 'status'}>
            <Typography variant="text-4" translation={null} block>
              {t(
                loadFailed
                  ? 'No-show protection could not be loaded. Please try again.'
                  : 'Loading no-show protection…'
              )}
            </Typography>
          </div>
          {loadFailed && (
            <Button
              type="button"
              fullWidth
              variant="outlined"
              translation={null}
              onClick={() => setAttempt((n) => n + 1)}
            >
              {t('Try again')}
            </Button>
          )}
        </>
      ) : (
        <>
          <Typography variant="text-4" color="subdued" translation={null} block>
            {t('Template')}: {template.name}
          </Typography>
          <details className="staff-protection-terms">
            <summary>{t('Show conditions to read to the guest')}</summary>
            <div className="staff-protection-terms-copy">
              {template.guestText}
            </div>
          </details>
          {!waived && (
            <Typography variant="text-4" translation={null} block>
              {t(
                'After saving, the guest receives an SMS with a link to accept the conditions and deposit a payment method. No email is sent for this protection link.'
              )}
            </Typography>
          )}
          {!waived && (
            <>
              <Typography variant="text-4" translation={null} block>
                {t('SMS recipient')}:{' '}
                {reservation.guest?.phone || t('No mobile number entered')}
              </Typography>
              <Checkbox
                checked={reservation.guest?.sendSms === true}
                onChange={(_, sendSms) =>
                  onReservationChange((old) =>
                    !old || !old.guest
                      ? old
                      : { ...old, guest: { ...old.guest, sendSms } }
                  )
                }
                label={t(
                  'The guest agreed to receiving the protection link by SMS'
                )}
                translation={null}
              />
              {error && (
                <div className="staff-protection-error" role="alert">
                  {t(error)}
                </div>
              )}
              <Typography
                variant="text-4"
                color="subdued"
                translation={null}
                block
              >
                {t(
                  'The reservation stays pending until the payment method is deposited. The guest has {{minutes}} minutes, at most until the reservation starts. Otherwise it is cancelled.',
                  { minutes: template.depositDeadlineMinutes }
                )}
              </Typography>
            </>
          )}
          {canCharge && (
            <div className="staff-protection-waiver">
              <Checkbox
                checked={waived}
                onChange={(_, noShowProtectionWaive) =>
                  onReservationChange((old) =>
                    !old ? null : { ...old, noShowProtectionWaive }
                  )
                }
                label={t('Release without no-show protection')}
                translation={null}
              />
              {waived && (
                <Typography
                  variant="text-4"
                  color="subdued"
                  translation={null}
                  block
                >
                  {t(
                    'The reservation will be released without protection. This is documented.'
                  )}
                </Typography>
              )}
            </div>
          )}
        </>
      )}
    </Box>
  );
};

export default StaffProtectionOption;
